import * as Sentry from '@sentry/react';
const sentryEnvironment = 'uat';
export function initializeSentry() {
  // Must be REACT_APP_-prefixed so CRA/CRACO inlines it into the browser bundle.
  const dsn = process.env.REACT_APP_SENTRY_FRONTEND;

  if (!dsn) {
    console.log('Sentry DSN not configured - error tracking disabled');
    return;
  }

  Sentry.init({
    dsn: dsn,

    // Set environment (development, staging, production, etc.)
    // REACT_APP_ENV lets you distinguish staging vs production in built apps,
    // since NODE_ENV is always "production" for any `npm run build` output.
    environment: sentryEnvironment,

    // Release tracking for identifying which deployment introduced errors
    release: process.env.REACT_APP_SENTRY_RELEASE || `carboncare-dashboard@${process.env.REACT_APP_VERSION || 'unknown'}`,

    // Enable Sentry Logs (shows in Sentry > Logs)
    enableLogs: true,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text and inputs by default for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
      // Forward console.log/warn/error to Sentry Logs
      Sentry.consoleLoggingIntegration(),
    ],

    // Performance Monitoring - sample rate
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1, // 10% in prod, 100% in dev

    // Session Replay - sample rate
    replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1,
    replaysOnErrorSampleRate: 1, // 100% replay on errors

    // Filter out sensitive data
    beforeSend(event, _hint) {
      // Remove sensitive data from URLs
      if (event.request?.url) {
        // Remove query parameters that might contain sensitive data
        event.request.url = event.request.url.split('?')[0];
      }

      // Remove auth tokens from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
          if (breadcrumb.data) {
            delete breadcrumb.data.authToken;
            delete breadcrumb.data.token;
            delete breadcrumb.data.password;
          }
          return breadcrumb;
        });
      }

      return event;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions errors
      'top.GLOBALS',
      // Network errors
      'NetworkError',
      'Network request failed',
      // React errors that are expected
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });

  console.log(`Sentry initialized for ${sentryEnvironment} environment`);
}

// Helper to capture exceptions with context
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    contexts: context ? { custom: context } : undefined,
  });
}

// Helper to capture messages (non-error events)
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) {
  Sentry.captureMessage(message, {
    level,
    contexts: context ? { custom: context } : undefined,
  });
}

// Helper to set user context
export function setUser(user: { id?: string; email?: string; username?: string }) {
  Sentry.setUser(user);
}

// Helper to set tags for filtering
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

// Helper to set extra context
export function setContext(name: string, context: Record<string, any>) {
  Sentry.setContext(name, context);
}

// Submit a user-reported bug to Sentry with structured metadata and tags
export function submitBugReport(data: {
  title: string;
  description: string;
  severity: 'info' | 'warning' | 'error' | 'fatal';
  page?: string;
  userEmail?: string;
  userName?: string;
}) {
  Sentry.withScope((scope) => {
    scope.setTag('type', 'bug_report');
    scope.setTag('severity', data.severity);
    scope.setTag('page', data.page || globalThis.location.pathname);
    scope.setLevel(data.severity);
    scope.setContext('bug_report', {
      title: data.title,
      description: data.description,
      page: data.page || globalThis.location.pathname,
      userAgent: navigator.userAgent,
    });
    if (data.userEmail || data.userName) {
      scope.setUser({ email: data.userEmail, username: data.userName });
    }
    // Bug reports go through User Feedback (not Issues) so the tags above can
    // drive filtering/alerts and a replay attaches when one is active.
    Sentry.captureFeedback({
      name: data.userName || 'Anonymous',
      email: data.userEmail || '',
      message: `[${data.severity.toUpperCase()}] ${data.title}\n\n${data.description}`,
      url: globalThis.location.href,
    });
  });
}

// Submit user feedback to Sentry's feedback channel with category tagging
export function submitFeedback(data: {
  category: string;
  message: string;
  userEmail?: string;
  userName?: string;
}) {
  Sentry.withScope((scope) => {
    scope.setTag('type', 'user_feedback');
    scope.setTag('category', data.category);
    Sentry.captureFeedback({
      name: data.userName || 'Anonymous',
      email: data.userEmail || '',
      message: `[${data.category.toUpperCase()}] ${data.message}`,
      url: globalThis.location.href,
    });
  });
}

// ── Structured Logging ──
// Sentry structured logs (visible in Sentry Logs explorer)
export const logger = {
  trace: (message: string, attrs?: Record<string, unknown>) =>
    Sentry.logger.trace(message, attrs),
  debug: (message: string, attrs?: Record<string, unknown>) =>
    Sentry.logger.debug(message, attrs),
  info: (message: string, attrs?: Record<string, unknown>) =>
    Sentry.logger.info(message, attrs),
  warn: (message: string, attrs?: Record<string, unknown>) =>
    Sentry.logger.warn(message, attrs),
  error: (message: string, attrs?: Record<string, unknown>) =>
    Sentry.logger.error(message, attrs),
  fatal: (message: string, attrs?: Record<string, unknown>) =>
    Sentry.logger.fatal(message, attrs),
};

// ── Custom Metrics ──
// Sentry metrics ingestion was removed in @sentry/browser v8. These helpers
// are kept as forwarders to structured logs so existing call sites compile
// and the values remain queryable in the Logs explorer.
export const metrics = {
  increment: (name: string, value?: number, attributes?: Record<string, string>) =>
    Sentry.logger.info(name, { metric_type: 'counter', value: value ?? 1, ...attributes }),

  distribution: (name: string, value: number, unit?: string, attributes?: Record<string, string>) =>
    Sentry.logger.info(name, { metric_type: 'distribution', value, unit, ...attributes }),

  gauge: (name: string, value: number, attributes?: Record<string, string>) =>
    Sentry.logger.info(name, { metric_type: 'gauge', value, ...attributes }),
};

export { Sentry };
